import { BehaviorSubject } from 'rxjs';
import { initFilterState, setFilter, TableContext, updateHiddenColumns } from '@markant-platform/shared/data-access';
import { Store } from '@ngrx/store';
import { DestroyRef, Directive, inject, Inject, InjectionToken, OnInit, Optional } from '@angular/core';
import {
  MappedTableData,
  PaginationPageSize,
  SortAttributes,
  SortDirections,
  TableDraft,
  TableParamConfig,
} from '@markant-platform/shared/table/model';
import { MarkantFilter } from '@markant/elements';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MarkantSelectOption } from '@markant-platform/shared/util-helpers';
import { TableService } from './table.service';

export const TABLE_CONTEXT = new InjectionToken<string>('context');

@Directive()
export abstract class Table implements OnInit {
  tableDraft!: TableDraft;

  tableParams$: BehaviorSubject<TableParamConfig>;
  tableData$ = new BehaviorSubject<MappedTableData>({
    headerCells: [],
    showCheckBoxes: false,
    showRadioButtons: false,
    sortColumnIds: [],
    sortStartColumnId: '',
    sortStartDirection: 'asc',
    tableRows: [],
  });
  isLoading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  preSelectedOptions: Record<string, string[]> = {};
  totalCount = 0;
  protected readonly DEFAULT_THRESHOLD_FOR_SEARCHABLE_FILTER = 6;
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly store = inject(Store, { optional: true });
  protected readonly tableService = inject(TableService);

  private readonly DEFAULT_TABLE_DATA: TableParamConfig = {
    page: {
      index: 1,
      size: 25,
    },
    searchQuery: '',
    sort: { sortField: '', sortOrder: 'asc' },
    activeFilter: {},
  };
  private readonly _value$: BehaviorSubject<unknown[]> = new BehaviorSubject([] as unknown[]);

  protected constructor(@Optional() @Inject(TABLE_CONTEXT) private readonly context?: TableContext) {
    this.tableParams$ = new BehaviorSubject(this.DEFAULT_TABLE_DATA);
  }

  keepOrder = (): number => {
    return 0;
  };

  ngOnInit(): void {
    if (this.store && this.context) {
      initFilterState(
        this.store,
        this.context,
        this.preSelectedOptions,
        this.getDefaultSortingOptions(),
        this.tableDraft.hiddenColumns
      )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(params => {
          this.tableDraft.hiddenColumns = params.hiddenColumns;
          this.tableParams$.next(params);
        });
    } else {
      this.tableParams$.next({
        ...this.DEFAULT_TABLE_DATA,
        activeFilter: this.preSelectedOptions,
        sort: this.getDefaultSortingOptions(),
        hiddenColumns: this.tableDraft.hiddenColumns,
      });
    }
  }

  reload(data: unknown[] | undefined = undefined): void {
    if (data) {
      this._value$.next(data);
    }

    this.update();
    this.isLoading$.next(false);
  }

  onSearchChange(value = ''): void {
    const searchValue = value?.trim();
    if (searchValue.length !== 1) {
      const updateTableParams = {
        ...this.tableParams$.getValue(),
        searchQuery: searchValue,
      };

      this.tableParams$.next(updateTableParams);
      this.setFilter(updateTableParams);
    }
  }

  /**
   * @deprecated
   * This function should be replaced with onFilterGroupChange when implementing the markant-filter-group
   */
  onFilterChange(filter: Record<string, string[]>): void {
    const updatedTableParams = {
      ...this.tableParams$.getValue(),
      activeFilter: filter,
    };

    this.tableParams$.next(updatedTableParams);
    this.setFilter(updatedTableParams);
  }

  onResetFilters(filters: MarkantFilter[]): void {
    if (!filters.length) {
      this.tableParams$.next({
        ...this.tableParams$.getValue(),
        activeFilter: {},
      });
    }
  }

  onFilterGroupChange(filters: MarkantFilter[], categoryKey: string): void {
    const { activeFilter, ...tableParams } = this.tableParams$.getValue();
    const filtersByCategory = filters
      .filter(({ category }) => (category?.value ?? '') === categoryKey)
      .map(({ value }) => value);
    const updatedActiveFilter = {
      ...activeFilter,
      [categoryKey]: filtersByCategory,
    };

    if (filtersByCategory.length === 0) {
      delete updatedActiveFilter[categoryKey];
    }

    this.tableParams$.next({
      ...tableParams,
      activeFilter: updatedActiveFilter,
    });

    this.setFilter({ ...tableParams, activeFilter: updatedActiveFilter });
  }

  onPageSizeChange(size?: PaginationPageSize): void {
    if (size) {
      const { page, ...current } = this.tableParams$.getValue();
      this.tableParams$.next({
        ...current,
        page: { ...page, size },
      });
    }
  }

  onIndexChange(index?: number): void {
    if (index) {
      const { page, ...current } = this.tableParams$.getValue();
      this.tableParams$.next({
        ...current,
        page: { ...page, index },
      });
    }
  }

  onSortChange({ columnId, sortDirection }: { columnId: string; sortDirection: SortDirections }): void {
    const updatedTableParams = {
      ...this.tableParams$.getValue(),
      sort: {
        sortField: columnId,
        sortOrder: sortDirection,
      },
    };

    this.tableParams$.next(updatedTableParams);
    this.setFilter(updatedTableParams);
  }

  onSearchbarValueChange(
    searchValue: string,
    value: { filterOptions: MarkantSelectOption[]; searchedFilterOptions?: MarkantSelectOption[] }
  ): void {
    value.searchedFilterOptions = value.filterOptions.filter(option => {
      return (
        option.value.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()) ||
        option.representation.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())
      );
    });
  }

  onColumnSelectionChange(columns: string[]): void {
    const hiddenColumns = this.tableDraft.headerAndTableCells
      .map(({ headerCellColumnId }) => headerCellColumnId)
      .filter(columnId => columnId !== 'actions')
      .filter(columnId => !columns.includes(columnId));

    this.tableDraft.hiddenColumns = hiddenColumns;
    this.update();

    if (this.store && this.context) {
      this.store.dispatch(updateHiddenColumns({ tableContext: this.context, hiddenColumns }));
    }

    this.tableParams$.next({
      ...this.tableParams$.getValue(),
      hiddenColumns,
    });
  }

  private update(): void {
    this.tableData$.next(this.tableService.returnMappedTableData(this._value$.getValue(), this.tableDraft));
  }

  private getDefaultSortingOptions(): SortAttributes | undefined {
    if (!this.tableDraft.sortStartColumnId) {
      return undefined;
    }

    return {
      sortField: this.tableDraft.sortStartColumnId,
      sortOrder: this.tableDraft.sortStartDirection ?? 'desc',
    };
  }

  private setFilter({ hiddenColumns: _, ...tableParams }: TableParamConfig): void {
    if (this.store && this.context) {
      this.store.dispatch(
        setFilter({
          key: this.context,
          filter: tableParams,
        })
      );
    }
  }

  protected patchActiveFilters(addedFilters: Record<string, string[]>): void {
    this.tableParams$.next({
      ...this.tableParams$.value,
      activeFilter: {
        ...this.tableParams$.value.activeFilter,
        ...addedFilters,
      },
    });
  }
}
